import { LitElement, html } from 'lit';
import styles from './studio-button.css.mjs';
import '../studio-icon/studio-icon.mjs';

export class StudioButton extends LitElement {
  static styles = styles;

  static properties = {
    size: { type: String, reflect: true },
    state: { type: String, reflect: true },
    styleType: { type: String, reflect: true, attribute: 'style-type' },
    icon: { type: String, reflect: true },
    iconOnly: { type: Boolean, reflect: true, attribute: 'icon-only' },
    position: { type: String, reflect: true },
    backgroundColor: { type: String, reflect: true, attribute: 'background-color' },
    textColor: { type: String, reflect: true, attribute: 'text-color' },
    borderColor: { type: String, reflect: true, attribute: 'border-color' },
  };

  static validSizes = ['giant', 'large', 'medium', 'small', 'tiny'];
  static validStates = ['default', 'focus', 'press', 'disabled', 'selected', 'hover'];
  static validStyleTypes = ['filled', 'outline', 'clear', 'menu'];
  static validPositions = ['left', 'center', 'right', ''];

  constructor() {
    super();
    this.size = 'medium';
    this.state = 'default';
    this.styleType = '';
    this.icon = '';
    this.iconOnly = false;
    this.position = '';
    this.backgroundColor = '';
    this.textColor = '';
    this.borderColor = '';
  }

  set size(value) {
    const oldValue = this._size;
    this._size = StudioButton.validSizes.includes(value) ? value : 'medium';
    this.requestUpdate('size', oldValue);
  }

  get size() {
    return this._size;
  }

  set state(value) {
    this._state = StudioButton.validStates.includes(value) ? value : 'default';
  }

  get state() {
    return this._state;
  }

  set styleType(value) {
    this._styleType = StudioButton.validStyleTypes.includes(value) ? value : '';
  }

  get styleType() {
    return this._styleType;
  }

  set position(value) {
    this._position = StudioButton.validPositions.includes(value) ? value : '';
  }

  get position() {
    return this._position;
  }

  firstUpdated() {
    this._button = this.renderRoot.querySelector('button');
    this._reflectAttributes();

    if (!this._button || this.hasAttribute('state')) return;

    this._setState = (value) => {
      if (this.state !== 'disabled') this.state = value;
    };

    this._eventListeners = [
      { event: 'focus', handler: () => this._setState('focus') },
      { event: 'mouseenter', handler: () => this._setState('hover') },
      { event: 'mouseleave', handler: () => this._setState('default') },
      { event: 'blur', handler: () => this._setState('default') },
      { event: 'mousedown', handler: () => this._setState('press') },
      { event: 'active', handler: () => this._setState('press') },
      { event: 'mouseup', handler: () => this._setState('focus') },
    ];

    this._eventListeners.forEach(({ event, handler }) =>
      this._button.addEventListener(event, handler)
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._button && this._eventListeners) {
      this._eventListeners.forEach(({ event, handler }) =>
        this._button.removeEventListener(event, handler)
      );
    }
  }

  updated(changedProps) {
    super.updated(changedProps);
    this._reflectAttributes();

    if (changedProps.has('backgroundColor')) {
      this._updateVar('--button-bg-color', this.backgroundColor);
    }
    if (changedProps.has('textColor')) {
      this._updateVar('--button-text-color', this.textColor);
    }
    if (changedProps.has('borderColor')) {
      this._updateVar('--button-outline-color', this.borderColor);
    }
  }

  _updateVar(varName, value) {
    if (value) {
      this.style.setProperty(varName, value);
    } else {
      this.style.removeProperty(varName);
    }
  }

  _reflectAttributes() {
    const exclude = [
      'size',
      'state',
      'style-type',
      'icon',
      'icon-only',
      'position',
      'background-color',
      'text-color',
      'border-color',
    ];

    Array.from(this.attributes).forEach(attr => {
      if (!exclude.includes(attr.name)) {
        this._button.setAttribute(attr.name, attr.value);
      }
    });
  }

  render() {
    const isDisabled = this.state === 'disabled';
    const isSelected = this.state === 'selected';
    const iconOnly = this.iconOnly === true;

    return html`
      <button
        ?disabled=${isDisabled}
        part="button"
        exportparts="button"
        class=${this.position}
        aria-disabled=${isDisabled ? 'true' : 'false'}
        aria-pressed=${isSelected ? 'true' : 'false'}
      >
        <slot name="icon">
          ${this.icon ? html`<studio-icon name="${this.icon}"></studio-icon>` : ''}
        </slot>
        ${!iconOnly
          ? html`<slot name="text"><span class="text"><slot></slot></span></slot>`
          : ''}
      </button>
    `;
  }
}

customElements.define('studio-button', StudioButton);